import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  Alert,
  Breadcrumbs,
  Link,
  Checkbox,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  FormControlLabel,
  CircularProgress,
} from '@mui/material'
import { LoadingIndicator } from '@/apps/legacy/components/atoms/LoadingIndicator'
import AdvancedSearchBox from '@/apps/legacy/components/portal/AdvancedSearchBox'
import { AdvancedSearchCondition } from '@/shared/types/advancedSearch'
import PaginationControl from '@/apps/legacy/components/portal/PaginationControl'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import {
  useDrugGroupDetailQuery,
  useDrugGroupDrugs,
  useDrugSearchQuery,
} from '@/features/internal/drugs'
import { useToast } from '@/shared/contexts/ToastContext'
import { DRUG_SELECTION } from '@/shared/constants/const'

/**
 * 医薬品グループ用医薬品検索・追加画面
 */
const DrugGroupDrugsPage: React.FC = () => {
  // URLからグループIDを取得
  const { id: groupId = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // URLパラメータから検索条件とページ情報を取得
  const searchParams = new URLSearchParams(location.search)
  const searchParam = searchParams.get('search') || ''

  // 高度検索条件を解析する関数
  const parseAdvancedSearchParam = (param: string) => {
    if (!param.startsWith('[高度]')) {
      return { isAdvanced: false, normalKeyword: param, conditions: [] }
    }

    const summary = param.replace('[高度]', '')
    const conditions: AdvancedSearchCondition[] = []

    // "drug_name:カルボ+dosage_form:oral" のような形式を解析
    const parts = summary.split('+')

    parts.forEach((part, index) => {
      const [type, value] = part.split(':')
      if (type && value) {
        const typeConfig = {
          drug_name: { field: 'product_name', type: 'drug_name' as const },
          ingredient: { field: 'general_name', type: 'ingredient' as const },
          dosage_form: { field: 'dosage_form', type: 'dosage_form' as const },
          yj_code: { field: 'yj_code', type: 'yj_code' as const },
        }[type]

        if (typeConfig) {
          conditions.push({
            id: `condition-${index}`,
            type: typeConfig.type,
            field: typeConfig.field,
            value: value,
            operator: index === 0 ? 'and' : 'and',
          })
        }
      }
    })

    return { isAdvanced: true, normalKeyword: '', conditions }
  }

  // URLパラメータを解析
  const parsedSearch = parseAdvancedSearchParam(searchParam)

  // 医薬品グループ詳細クエリ（TanStack Query）
  const {
    data: selectedGroup,
    isLoading: groupLoading,
    error: groupError,
  } = useDrugGroupDetailQuery(groupId)

  const {
    loading: groupDrugsLoading,
    error: groupDrugsError,
    groupDrugs,
    selectedDrugIds,
    addDrugsToGroupAction,
    toggleDrugSelection,
    selectAllDrugs,
    selectAllSearchResults,
    selectAllAdvancedSearchResults,
    clearSelection,
  } = useDrugGroupDrugs(groupId)

  const { showToast } = useToast()

  // 共通状態
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [selectAllChecked, setSelectAllChecked] = useState(false)

  // 検索モード管理
  const [isAdvancedMode, setIsAdvancedMode] = useState(parsedSearch.isAdvanced)

  // 医薬品検索フック（TanStack Query）- 通常検索・高度検索を統一管理
  const {
    normalSearchKeyword,
    setNormalSearchKeyword,
    advancedConditions,
    searchResults,
    isLoading: searchLoading,
    error: searchError,
    executeNormalSearch,
    executeAdvancedSearch,
    currentPage: searchCurrentPage,
    setCurrentPage: setSearchCurrentPage,
    resetSearch,
  } = useDrugSearchQuery({
    initialSearchKeyword: parsedSearch.normalKeyword,
  })

  // ページ初期化フラグ（初回ロードとリロード時のみURL復元を行う）
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // 検索状態を再初期化（URLパラメータから）- 初回ロード時のみ
  useEffect(() => {
    // 初回ロード時のみURL復元を行う
    if (!isInitialLoad) {
      return
    }

    // URLパラメータから最新の値を取得
    const params = new URLSearchParams(location.search)
    const newSearchParam = params.get('search') || ''
    const newPageParam = params.get('page')
      ? parseInt(params.get('page') || '1', 10)
      : 1

    // URLパラメータを解析
    const newParsedSearch = parseAdvancedSearchParam(newSearchParam)

    // useEffect内で実行する処理を整理
    const initializeFromUrl = () => {
      // 高度検索の場合は条件を復元
      if (newParsedSearch.isAdvanced && newParsedSearch.conditions.length > 0) {
        // 検索キーワードを復元（高度検索の場合はdrug_nameの値を使用）
        const drugNameCondition = newParsedSearch.conditions.find(
          (c) => c.type === 'drug_name'
        )
        if (drugNameCondition) {
          setNormalSearchKeyword(drugNameCondition.value)
        }

        // 高度検索を実行
        executeAdvancedSearch(newParsedSearch.conditions, newPageParam)
      } else if (newParsedSearch.normalKeyword) {
        // 通常検索を実行
        executeNormalSearch(newParsedSearch.normalKeyword, newPageParam)
      }

      // 初期化完了
      setIsInitialLoad(false)
    }

    initializeFromUrl()
  }, [
    executeAdvancedSearch,
    executeNormalSearch,
    setNormalSearchKeyword,
    isInitialLoad,
    location.search,
  ])

  // グループ内の医薬品はuseDrugGroupDrugs(groupId)でgroupId変更時に自動取得

  // 通常検索ハンドラー
  const handleNormalSearch = useCallback(() => {
    if (!normalSearchKeyword) return

    // 検索条件変更時に選択状態をクリア
    clearSelection()
    setSelectAllChecked(false)

    // 通常検索実行
    executeNormalSearch(normalSearchKeyword, 1)

    // URLパラメータを更新
    const params = new URLSearchParams()
    params.set('search', normalSearchKeyword)
    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
      },
      { replace: true }
    )
  }, [
    normalSearchKeyword,
    clearSelection,
    executeNormalSearch,
    navigate,
    location.pathname,
  ])

  // 統一されたページネーション処理
  const handlePageChange = useCallback(
    (newPage: number) => {
      if (isAdvancedMode) {
        // 高度検索のページネーション
        setSearchCurrentPage(newPage)
        if (advancedConditions.length > 0) {
          executeAdvancedSearch(advancedConditions, newPage)
        }
      } else {
        // 通常検索のページネーション
        if (normalSearchKeyword) {
          executeNormalSearch(normalSearchKeyword, newPage)
        }
      }
    },
    [
      isAdvancedMode,
      advancedConditions,
      normalSearchKeyword,
      setSearchCurrentPage,
      executeAdvancedSearch,
      executeNormalSearch,
    ]
  )

  // 高度検索ハンドラー
  const handleAdvancedSearch = useCallback(
    (conditions: AdvancedSearchCondition[]) => {
      // 検索条件変更時に選択状態をクリア
      clearSelection()
      setSelectAllChecked(false)

      executeAdvancedSearch(conditions)

      // 高度検索の場合は検索条件の概要をURLパラメータに含める
      const summary = conditions
        .filter((c) => c.value.trim())
        .map((c) => `${c.type}:${c.value}`)
        .join('+')
      const params = new URLSearchParams()
      params.set('search', `[高度]${summary}`)
      navigate(
        {
          pathname: location.pathname,
          search: params.toString(),
        },
        { replace: true }
      )
    },
    [executeAdvancedSearch, navigate, location.pathname, clearSelection]
  )

  // 検索モード切り替え時のリセット処理
  const handleModeChange = useCallback(
    (isAdvanced: boolean) => {
      // 共通状態をリセット
      clearSelection()
      setSelectAllChecked(false)

      // モードを設定
      setIsAdvancedMode(isAdvanced)

      // 検索状態をリセット（通常検索・高度検索共通）
      resetSearch()

      // URLもクリア（検索結果なしの状態にする）
      navigate(
        {
          pathname: location.pathname,
          search: '',
        },
        { replace: true }
      )
    },
    [clearSelection, resetSearch, navigate, location.pathname]
  )

  // 医薬品選択処理
  const handleDrugSelect = (drugId: string) => {
    toggleDrugSelection(drugId)
  }

  // 全選択/解除処理
  const handleSelectAll = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked
    setSelectAllChecked(checked)

    if (checked) {
      // 「全て選択」実行前に既存の選択をクリア（混在を防ぐ）
      clearSelection()

      if (isAdvancedMode && displayData.hasSearch) {
        // 高度検索の全件選択
        try {
          const result = await selectAllAdvancedSearchResults(
            advancedConditions
          )
          if (result.totalFound > DRUG_SELECTION.MAX_SELECTION_COUNT) {
            setSelectAllChecked(false)
          }
          showSelectAllMessage(result)
        } catch (_error) {
          showToast('全件選択に失敗しました', 'error')
          setSelectAllChecked(false)
        }
      } else if (!isAdvancedMode && displayData.hasSearch) {
        // 通常検索の全件選択
        try {
          const result = await selectAllSearchResults(normalSearchKeyword)
          if (result.totalFound > DRUG_SELECTION.MAX_SELECTION_COUNT) {
            setSelectAllChecked(false)
          }
          showSelectAllMessage(result)
        } catch (_error) {
          showToast('全件選択に失敗しました', 'error')
          setSelectAllChecked(false)
        }
      } else {
        // 検索していない場合は、現在表示中の薬剤を選択
        selectAllDrugs(displayData.drugs)
      }
    } else {
      // 選択解除の場合は常に全選択をクリア
      clearSelection()
    }
  }

  // 全件選択結果メッセージの表示
  const showSelectAllMessage = (result: {
    totalFound: number
    selectedCount: number
    alreadyInGroup: number
  }) => {
    if (result.totalFound > DRUG_SELECTION.MAX_SELECTION_COUNT) {
      showToast(
        `${DRUG_SELECTION.MAX_SELECTION_COUNT}件以上の医薬品は一括選択できません`,
        'warning'
      )
      return
    }

    showToast(`${result.selectedCount}件の薬剤を選択しました`, 'success')
  }

  // 追加して保存
  const handleSave = async () => {
    try {
      if (selectedDrugIds.length === 0) {
        alert('薬剤が選択されていません')
        return
      }

      const success = await addDrugsToGroupAction(groupId)

      if (success) {
        setSaveSuccess(true)

        // 成功メッセージを表示した後に詳細画面へ戻る
        setTimeout(() => {
          navigate(`/portal/soaps/groups/${groupId}`)
        }, 2000)
      }
    } catch (_err) {
      // Handle error silently
    }
  }

  // キャンセル処理
  const handleCancel = () => {
    if (
      selectedDrugIds.length > 0 &&
      !window.confirm('選択した薬剤が破棄されますが、よろしいですか？')
    ) {
      return
    }

    // 詳細画面へ戻る
    navigate(`/portal/soaps/groups/${groupId}`)
  }

  // 統一された表示データインターフェース（両検索モード共通）
  const displayData = {
    drugs: searchResults?.results || [],
    loading: searchLoading,
    error: searchError?.message ?? null,
    currentPage: searchCurrentPage,
    totalPages: searchResults
      ? Math.ceil(searchResults.count / (searchResults.page_size || 20))
      : 1,
    hasSearch: isAdvancedMode
      ? advancedConditions.length > 0
      : !!normalSearchKeyword,
  }

  // ローディング状態
  const isLoading = displayData.loading || groupLoading || groupDrugsLoading

  // エラーがあるかどうか
  const hasError = !!displayData.error || !!groupError || !!groupDrugsError

  // 「全て選択」チェックボックスの状態を管理
  const selectAllCheckedState = useMemo(() => {
    if (displayData.drugs.length === 0) {
      return false
    }

    // 現在表示中の薬剤のうち、選択可能な薬剤（グループに未追加）を取得
    const selectableDrugs = displayData.drugs.filter(
      (drug) => !groupDrugs?.some((groupDrug) => groupDrug.id === drug.id)
    )

    // 選択可能な薬剤がすべて選択されているかチェック
    return (
      selectableDrugs.length > 0 &&
      selectableDrugs.every((drug) => selectedDrugIds.includes(drug.id))
    )
  }, [displayData.drugs, selectedDrugIds, groupDrugs])

  // useMemoの結果を状態に反映（派生状態の管理）
  useEffect(() => {
    setSelectAllChecked(selectAllCheckedState)
  }, [selectAllCheckedState])

  return (
    <PortalLayout>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* パンくずリスト */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            onClick={(e) => {
              e.preventDefault()
              navigate('/portal/soaps')
            }}
            style={{ cursor: 'pointer' }}
          >
            SOAPテンプレート管理
          </Link>
          <Link
            color="inherit"
            onClick={(e) => {
              e.preventDefault()
              navigate(`/portal/soaps/groups/${groupId}`)
            }}
            style={{ cursor: 'pointer' }}
          >
            {selectedGroup?.name || '医薬品グループ'}
          </Link>
          <Typography color="text.primary">医薬品追加</Typography>
        </Breadcrumbs>

        {/* ヘッダー部分 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            「{selectedGroup?.name || '医薬品グループ'}」に医薬品を追加
          </Typography>
          <Divider />
        </Box>

        {/* 成功メッセージ */}
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            選択した医薬品がグループに追加されました。
          </Alert>
        )}

        {/* エラー表示 */}
        {hasError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            操作中にエラーが発生しました。しばらくしてから再度お試しください。
          </Alert>
        )}

        {/* 検索部分 */}
        <Box sx={{ mb: 3 }}>
          <AdvancedSearchBox
            normalSearchValue={normalSearchKeyword}
            onNormalSearchChange={setNormalSearchKeyword}
            onNormalSearch={handleNormalSearch}
            onAdvancedSearch={handleAdvancedSearch}
            placeholder="医薬品名またはYJコードで検索"
            isAdvancedMode={isAdvancedMode}
            onAdvancedModeChange={handleModeChange}
            initialAdvancedConditions={
              parsedSearch.isAdvanced
                ? parsedSearch.conditions
                : advancedConditions
            }
          />
        </Box>

        {/* 選択状態表示 */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle1">
            選択中:{' '}
            <Chip label={selectedDrugIds.length} color="primary" size="small" />
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAllChecked}
                onChange={handleSelectAll}
                disabled={
                  displayData.drugs.length === 0 || !displayData.hasSearch
                }
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                全て選択
                {displayData.hasSearch && selectAllChecked && (
                  <Chip
                    label="検索結果全件"
                    color="primary"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
                {isLoading && selectAllChecked && (
                  <CircularProgress size={16} sx={{ ml: 1 }} />
                )}
              </Box>
            }
          />
        </Box>

        {/* ローディング表示 */}
        {isLoading && (
          <Box my={4}>
            <LoadingIndicator />
          </Box>
        )}

        {!isLoading && (
          <>
            {/* 医薬品テーブル */}
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>YJコード</TableCell>
                    <TableCell>医薬品名</TableCell>
                    <TableCell>製造元</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayData.drugs.length > 0 ? (
                    displayData.drugs.map((drug) => {
                      // すでにグループに所属している医薬品はチェックを無効化
                      const isAlreadyInGroup = groupDrugs?.some(
                        (groupDrug) => groupDrug.id === drug.id
                      )

                      return (
                        <TableRow key={drug.id}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedDrugIds.includes(drug.id)}
                              onChange={() => handleDrugSelect(drug.id)}
                              disabled={isAlreadyInGroup}
                            />
                          </TableCell>
                          <TableCell>{drug.yj_code}</TableCell>
                          <TableCell>
                            {drug.product_name ? (
                              drug.product_name
                            ) : (
                              <span style={{ color: '#666' }}>
                                【般】{drug.general_name}
                              </span>
                            )}
                            {isAlreadyInGroup && (
                              <Chip
                                label="すでに追加済み"
                                size="small"
                                color="default"
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>{drug.manufacturer}</TableCell>
                        </TableRow>
                      )
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        {displayData.hasSearch
                          ? '検索条件に一致する医薬品がありません'
                          : '医薬品を検索してください'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ページネーション */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <PaginationControl
                currentPage={displayData.currentPage}
                totalPages={displayData.totalPages}
                onPageChange={handlePageChange}
              />
            </Box>

            {/* アクションボタン */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 4,
              }}
            >
              <Button variant="outlined" onClick={handleCancel}>
                キャンセル
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isLoading || selectedDrugIds.length === 0}
                startIcon={
                  isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {selectedDrugIds.length}件追加して保存
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </PortalLayout>
  )
}

export default DrugGroupDrugsPage
